import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    getDataPrepStatus,
} from "../api/userRegistration";

const POLL_INTERVAL_MS = 30_000;

const SUCCESS_STATUSES = [
    "success",
    "completed",
    "finished",
];

const STOP_STATUSES = [
    "failed",
    "paused",
    ...SUCCESS_STATUSES,
];

export default function useDataPrepPolling({
    user,
    farm_id,
    status,
    setFarm,
}) {
    const pollRef = useRef(null);
    const pollingRef = useRef(false);

    const [bootstrapRun, setBootstrapRun] =
        useState(null);

    const stopPolling =
        useCallback(() => {
            if (pollRef.current) {
                clearInterval(
                    pollRef.current
                );

                pollRef.current = null;
            }
        }, []);

    const pollStatus =
        useCallback(async () => {
            if (pollingRef.current) {
                return null;
            }

            if (!user?.user_id || !farm_id) {
                stopPolling();
                return null;
            }

            pollingRef.current = true;

            try {
                const response =
                    await getDataPrepStatus(
                        user,
                        farm_id
                    );

                setBootstrapRun(
                    response ?? null
                );

                if (!response) {
                    return null;
                }

                const runStatus =
                    response.run?.status;

                const finished =
                    response.finished ||
                    SUCCESS_STATUSES.includes(
                        runStatus
                    );

                if (finished) {
                    stopPolling();

                    setFarm(prev => ({
                        ...prev,
                        farm_status:
                            "active",
                    }));

                    return response;
                }

                if (
                    STOP_STATUSES.includes(
                        runStatus
                    )
                ) {
                    stopPolling();
                }

                return response;
            } finally {
                pollingRef.current =
                    false;
            }
        }, [
            user,
            farm_id,
            setFarm,
            stopPolling,
        ]);

    const startPolling =
        useCallback(() => {
            if (pollRef.current) {
                return;
            }

            pollRef.current =
                setInterval(
                    pollStatus,
                    POLL_INTERVAL_MS
                );
        }, [pollStatus]);

    useEffect(() => {
        if (
            status !== "data_setup" ||
            !user?.user_id ||
            !farm_id
        ) {
            stopPolling();
            return;
        }

        let cancelled = false;

        const init =
            async () => {
                const response =
                    await pollStatus();

                if (cancelled) {
                    return;
                }

                if (
                    response?.run?.status ===
                    "running"
                ) {
                    startPolling();
                }
            };

        init();

        const onFocus =
            () => {
                pollStatus();
            };

        window.addEventListener(
            "focus",
            onFocus
        );

        return () => {
            cancelled = true;

            stopPolling();

            window.removeEventListener(
                "focus",
                onFocus
            );
        };
    }, [
        status,
        user?.user_id,
        farm_id,
        pollStatus,
        startPolling,
        stopPolling,
    ]);

    return {
        bootstrapRun,
        retryRun: pollStatus,
        stopPolling,
    };
}